import React from 'react';
import { AsyncSelect, BaseCheckbox, ISelectOption } from '../../../base';
import { IColumnFilterCustomProps } from '../filters/filters.models';

export interface IAssigneeFilterProps extends IColumnFilterCustomProps {
    /**
     * Function to load options for the async select
     */
    loadOptions: (searchTerm: string) => Promise<ISelectOption[]>;
    /**
     * Current user ID to show "Self" option
     */
    currentUserId?: string;
    /**
     * Label for the "Self" checkbox (default: "Self")
     */
    currentUserLabel?: string;
    /**
     * Placeholder text for the async select (default: "Search users...")
     */
    placeholder?: string;
}

/**
 * Assignee filter component with "Self" checkbox option.
 * 
 * This component provides a multi-select async select for choosing assignees
 * with an additional "Self" checkbox for quickly selecting the current user.
 * 
 * The filter value is stored as an array of user IDs (strings) for efficient
 * serialization and storage.
 */
export const AssigneeFilter: React.FC<IAssigneeFilterProps> = ({
    value,
    onChange,
    loadOptions,
    currentUserId,
    currentUserLabel = 'Self',
    placeholder = 'Search users...',
}) => {
    // Normalize value to always be an array
    const normalizedValue = React.useMemo(() => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        return [value];
    }, [value]);

    // Create self option if currentUserId is provided
    const selfOption: ISelectOption | undefined = currentUserId
        ? { label: currentUserLabel, value: currentUserId }
        : undefined;

    // Extract stored IDs from value (handles both strings and ISelectOption objects)
    const storedIds = React.useMemo(() => {
        return normalizedValue
            .map(v => {
                if (typeof v === 'string') return v;
                if (typeof v === 'object' && v !== null && 'value' in v) {
                    return String(v.value || '');
                }
                return String(v || '');
            })
            .filter(id => id !== '');
    }, [normalizedValue]);

    // Check if self is selected
    const isSelfSelected = React.useMemo(() => {
        if (!selfOption || !currentUserId) return false;
        const currentUserIdStr = String(currentUserId);
        return storedIds.some(id => String(id) === currentUserIdStr);
    }, [selfOption, currentUserId, storedIds]);

    // Extract IDs from ISelectOption objects
    const extractIds = (options: ISelectOption[]): string[] => {
        return options.map(opt => String(opt.value || '')).filter(id => id !== '');
    };

    // Handle user selection change from AsyncSelect
    const handleUserChange = (selected: any) => {
        const users = Array.isArray(selected) ? [...selected] : selected ? [selected] : [];
        const ids = extractIds(users);
        onChange(ids);
    };

    // Handle self checkbox toggle
    const handleSelfToggle = (checked: boolean) => {
        if (!selfOption) return;

        const selfIncluded = storedIds.includes(String(currentUserId || ''));

        if (checked && !selfIncluded) {
            onChange([...storedIds, String(currentUserId)]);
        } else if (!checked && selfIncluded) {
            onChange(storedIds.filter(id => id !== String(currentUserId)));
        }
    };

    // Load all options to map stored IDs back to ISelectOption objects
    const [allOptions, setAllOptions] = React.useState<ISelectOption[]>([]);

    React.useEffect(() => {
        loadOptions('')
            .then(options => {
                setAllOptions(options);
            })
            .catch(() => {
                // Silently handle errors
            });
    }, [loadOptions, value]); // Re-load when value changes (filter reopened)

    // Map stored IDs to full ISelectOption objects for display
    const selectedUsersForDisplay = React.useMemo(() => {
        if (storedIds.length === 0) return [];

        return storedIds
            .map(id => {
                const idStr = String(id);
                const found = allOptions.find(opt => String(opt.value) === idStr);
                if (found) return found;

                // For "Self", use the selfOption
                if (idStr === String(currentUserId) && selfOption) {
                    return selfOption;
                }

                // Create placeholder for other IDs (will be replaced when options load)
                return { label: idStr, value: idStr };
            })
            .filter(Boolean) as ISelectOption[];
    }, [storedIds, allOptions, currentUserId, selfOption]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
                <AsyncSelect
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    onChange={handleUserChange}
                    loadOptions={loadOptions}
                    placeholder={placeholder}
                    defaultOptions={true}
                    cacheOptions={true}
                    value={selectedUsersForDisplay}
                    isMulti={true}
                />
            </div>
            {selfOption && (
                <div>
                    <BaseCheckbox
                        checked={isSelfSelected}
                        onChange={(e) => handleSelfToggle(e.target.checked)}
                        label={currentUserLabel}
                    />
                </div>
            )}
        </div>
    );
};

